import {
  Outlet,
  Link,
  createFileRoute,
  redirect,
  useNavigate,
} from "@tanstack/react-router";
import { useEffect } from "react";
import { Flag, Cpu, Lightbulb, Mic, LogOut, Sparkles, Telescope, Users } from "lucide-react";
import { useAuth } from "@/auth/use-auth";

export const Route = createFileRoute("/_authed")({
  beforeLoad: ({ context, location }) => {
    const status = context.auth.state.status;
    if (status === "loading") {
      // Wait for bootstrap before deciding. Returning a never-resolving
      // load on first render would freeze the UI; instead we let the
      // route render and gate inside the component on `loading`.
      return;
    }
    if (status !== "authenticated") {
      throw redirect({
        to: "/login",
        search: { redirect: location.href },
      });
    }
  },
  component: AuthedLayout,
});

function AuthedLayout() {
  const auth = useAuth();
  const navigate = useNavigate();

  // beforeLoad can't re-fire when auth resolves from "loading" → "anonymous"
  // after the route has already mounted, so the component owns that redirect.
  useEffect(() => {
    if (auth.state.status === "anonymous") {
      void navigate({ to: "/login", replace: true });
    }
  }, [auth.state.status, navigate]);

  if (auth.state.status === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-sm text-zinc-500">Loading…</p>
      </div>
    );
  }
  if (auth.state.status !== "authenticated") {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-sm text-zinc-500">Redirecting…</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen">
      <aside className="hidden w-60 shrink-0 border-r border-zinc-200 bg-white sm:block">
        <div className="flex h-14 items-center gap-2 border-b border-zinc-200 px-4">
          <img
            src={`${import.meta.env.BASE_URL}brand/2oodles-icon-light.svg`}
            alt=""
            aria-hidden
            className="h-6 w-6"
          />
          <span className="font-display text-base tracking-tight">
            2oodles · admin
          </span>
        </div>
        <nav className="px-2 py-3 text-sm">
          <NavLink to="/llms" icon={<Cpu className="h-4 w-4" />}>LLMs</NavLink>
          <NavLink to="/prompts" icon={<Sparkles className="h-4 w-4" />}>Prompts</NavLink>
          <NavLink to="/users" icon={<Users className="h-4 w-4" />}>Users</NavLink>
          <NavLink to="/commands" icon={<Mic className="h-4 w-4" />}>Talk to Toodles</NavLink>
          <NavLink to="/reports" icon={<Flag className="h-4 w-4" />}>Reports</NavLink>
          <NavLink to="/ideas" icon={<Lightbulb className="h-4 w-4" />}>Ideas</NavLink>
          <NavLink to="/think-tank" icon={<Telescope className="h-4 w-4" />}>Think Tank</NavLink>
        </nav>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 shrink-0 items-center justify-between border-b border-zinc-200 bg-white px-4">
          <span className="text-sm text-zinc-600">
            Signed in as{" "}
            <span className="font-medium text-zinc-900">
              {auth.state.user.email ?? auth.state.user.display_name}
            </span>
          </span>
          <button
            type="button"
            onClick={() => void auth.signOut()}
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm text-zinc-700 hover:bg-zinc-100"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </header>
        <main className="flex-1 overflow-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function NavLink({
  to,
  icon,
  children,
}: {
  to: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Link
      to={to}
      activeProps={{
        className:
          "flex items-center gap-2 rounded-md px-2 py-1.5 bg-zinc-100 text-zinc-900",
      }}
      inactiveProps={{
        className:
          "flex items-center gap-2 rounded-md px-2 py-1.5 text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900",
      }}
    >
      {icon}
      <span>{children}</span>
    </Link>
  );
}
